import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { SurveysIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getSurveysFilterParams } from '../lib/surveys-query'
import { useSurveysStore } from '../model/store'

interface SurveysApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/** Filters and pages change faster than the index answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortSurveysApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useSurveysStore().setLoading(false)
}

/** `GET /admin-api/surveys` — the index. Returns `{ surveys, meta }`, or `null` when failed or superseded. */
export const useSurveysApi = async (options?: SurveysApiOptions): Promise<SurveysIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useSurveysStore()

  const url = useAdminRoutes('admin.surveys.data', pick({
    ...getSurveysFilterParams(store.getFilters),
    discipline: store.getDisciplineId ?? undefined,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: SurveysIndexData | null = data.data || null

    if (indexData) {
      store.setSurveys(indexData.surveys)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
